import React, { useMemo } from 'react';
import { Video } from 'lucide-react';
import { ISLAND_PATH, VIEW, project, conePath, TEIDE, camName } from '@/lib/tenerife';
import { conditionView, conditionAria, COND_ICONS, NEUTRAL_COLOR, PILL_BORDER, TEMP_TEXT, STATE_COLORS } from '@/lib/conditions';
import { layoutPoints, DOT_R, PILL_H, PILL_FONT, PILL_ICON, PILL_PAD, HIT } from '@/lib/weatherLayout';

const CONE_R = 110;

const hideIconForLargeText = () => {
  if (typeof window === 'undefined') return false;
  const px = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  return px > 16 * 1.3;
};

export const TenerifeMap = ({ cams = [], selectedId, onSelect, language = 'es', className = '', showLabels = true, now = new Date() }) => {
  const teide = project(TEIDE.lat, TEIDE.lon);
  const withIcon = !hideIconForLargeText();

  const points = useMemo(() => {
    const items = cams.map((c) => ({ id: c.id, cam: c, name: camName(c, language), ...project(c.latitude, c.longitude), view: conditionView(c.condition, now) }));
    return layoutPoints(items, { view: VIEW, withIcon });
  }, [cams, now, withIcon, language]);

  return (
    <svg viewBox={`0 0 ${VIEW.w} ${VIEW.h}`} className={`w-full h-auto select-none ${className}`} role="group" aria-label="Tenerife" data-testid="tenerife-map">
      <defs>
        <radialGradient id="tnf-relief" cx={teide.x} cy={teide.y} r="190" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#1F7E7A" stopOpacity="0.55" />
          <stop offset="0.35" stopColor="#2E9D98" stopOpacity="0.28" />
          <stop offset="1" stopColor="#38B2AC" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="tnf-cone" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#38B2AC" stopOpacity="0.55" />
          <stop offset="1" stopColor="#38B2AC" stopOpacity="0.08" />
        </linearGradient>
        <filter id="tnf-shadow" x="-10%" y="-10%" width="120%" height="130%">
          <feDropShadow dx="0" dy="10" stdDeviation="12" floodColor="#0F4C4A" floodOpacity="0.18" />
        </filter>
        <filter id="tnf-dot-shadow" x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="#0F4C4A" floodOpacity="0.28" />
        </filter>
        <clipPath id="tnf-clip"><path d={ISLAND_PATH} /></clipPath>
      </defs>

      <path d={ISLAND_PATH} fill="#E6F4F3" stroke="#38B2AC" strokeWidth="2.5" filter="url(#tnf-shadow)" />
      <g clipPath="url(#tnf-clip)"><rect x="0" y="0" width={VIEW.w} height={VIEW.h} fill="url(#tnf-relief)" /></g>
      <path d={`M ${teide.x - 9} ${teide.y + 7} L ${teide.x} ${teide.y - 9} L ${teide.x + 9} ${teide.y + 7} Z`} fill="#1F7E7A" opacity="0.7" />

      {/* Conos: solo visuales, nunca capturan el toque */}
      <g pointerEvents="none">
        {points.map(({ cam, x, y }) => {
          const active = cam.id === selectedId;
          return (
            <path key={`cone-${cam.id}`} d={conePath(x, y, cam.heading ?? 0, cam.fov ?? 70, active ? CONE_R : CONE_R * 0.6)}
              fill={active ? 'url(#tnf-cone)' : '#38B2AC'} fillOpacity={active ? 1 : 0.14} stroke="#38B2AC" strokeOpacity={active ? 0.6 : 0.25} strokeWidth="1.5"
              style={{ transition: 'd 300ms ease, fill-opacity 250ms', opacity: selectedId && !active ? 0.45 : 1 }} data-testid={`webcam-cone-${cam.id}`} />
          );
        })}
      </g>

      {/* Líneas guía (solo si la pastilla no queda pegada) */}
      <g pointerEvents="none">
        {points.filter((p) => p.pill && p.pill.d > 0).map((p) => {
          const cx = Math.max(p.pill.x, Math.min(p.pill.x + p.pill.w, p.x));
          const cy = Math.max(p.pill.y, Math.min(p.pill.y + p.pill.h, p.y));
          return <line key={`guide-${p.cam.id}`} x1={p.x} y1={p.y} x2={cx} y2={cy} stroke={PILL_BORDER} strokeWidth="2" strokeOpacity={p.view.dimmed ? 0.35 : 0.7} />;
        })}
      </g>

      {/* Pastillas de temperatura */}
      {points.filter((p) => p.pill).map(({ cam, pill, view }) => {
        const Icon = COND_ICONS[view.icon];
        const dim = selectedId && cam.id !== selectedId;
        const color = view.dimmed ? STATE_COLORS.gris : TEMP_TEXT;
        const hitW = Math.max(HIT, pill.w), hitH = Math.max(HIT, pill.h);
        return (
          <g key={`pill-${cam.id}`} onClick={() => onSelect?.(cam)} className="cursor-pointer" data-testid={`webcam-pill-${cam.id}`} aria-hidden="true"
            style={{ transition: 'opacity 400ms ease', opacity: (view.dimmed ? 0.5 : 1) * (dim ? 0.75 : 1) }}>
            <rect x={pill.x} y={pill.y} width={pill.w} height={pill.h} rx={PILL_H / 2} fill="#FFFFFF" stroke={view.dimmed ? STATE_COLORS.gris : PILL_BORDER} strokeWidth="2.5" filter="url(#tnf-dot-shadow)" />
            <text x={pill.x + PILL_PAD} y={pill.y + PILL_H / 2 + PILL_FONT * 0.36} fontSize={PILL_FONT} fontWeight="800" fill={color} style={{ fontFamily: 'Manrope, sans-serif' }}>
              {view.temp}°
            </text>
            {withIcon && Icon && <Icon size={PILL_ICON} x={pill.x + pill.w - PILL_PAD - PILL_ICON} y={pill.y + (PILL_H - PILL_ICON) / 2} color={color} strokeWidth={2.4} />}
            <rect x={pill.x + pill.w / 2 - hitW / 2} y={pill.y + pill.h / 2 - hitH / 2} width={hitW} height={hitH} fill="transparent" />
          </g>
        );
      })}

      {points.map(({ cam, x, y, view, label }) => {
        const active = cam.id === selectedId;
        const dim = selectedId && !active;
        const fill = view.state ? view.color : NEUTRAL_COLOR;
        return (
          <g key={cam.id} onClick={() => onSelect?.(cam)} className="cursor-pointer" style={{ transition: 'opacity 250ms', opacity: dim ? 0.55 : 1 }}
            data-testid={`webcam-dot-${cam.id}`} data-state={view.state || 'none'} role="button" aria-pressed={active} aria-label={conditionAria(camName(cam, language), cam.condition, view, language)}>
            {active && <circle cx={x} cy={y} r={DOT_R + 16} fill={TEMP_TEXT} opacity="0.18" className="tnf-pulse" />}
            {active && <circle cx={x} cy={y} r={DOT_R + 7} fill="none" stroke={TEMP_TEXT} strokeWidth="5" />}
            <circle cx={x} cy={y} r={DOT_R} fill={fill} stroke="#FFFFFF" strokeWidth="3" filter="url(#tnf-dot-shadow)" style={{ transition: 'fill 400ms ease' }} />
            <Video size={DOT_R + 2} x={x - DOT_R / 2 - 1} y={y - DOT_R / 2 - 1} color="#FFFFFF" strokeWidth={2.4} aria-hidden="true" />
            <circle cx={x} cy={y} r={Math.max(DOT_R + 10, HIT / 2)} fill="transparent" />
            {showLabels && (
              <text x={label.tx} y={label.ty} fontSize="21" textAnchor={label.anchor} fontWeight={active ? 800 : 600} fill={active ? '#1D1D1F' : '#4A5568'} className="hidden sm:block"
                style={{ fontFamily: 'Manrope, sans-serif', paintOrder: 'stroke', stroke: '#FDFBF7', strokeWidth: 4 }}>
                {camName(cam, language)}
              </text>
            )}
          </g>
        );
      })}

    </svg>
  );
};

export default TenerifeMap;
